import { useTranslation } from 'react-i18next';
import type { TimedFeePlanVersion, TimedFeeVersion } from '@pokerroom/types';
import { useAuthStore } from '../../store/auth.store';
import { useFeePlans } from './TimedFeePlansPage';
import { TimedFeeSummary } from './TimedFeeSummary';

export function TimedFeePlanSelector({
  selectedId,
  current,
  templateId,
  onChange,
}: {
  selectedId: string;
  current?: TimedFeeVersion | null;
  templateId: string;
  onChange(version?: TimedFeePlanVersion): void;
}) {
  const { t } = useTranslation('tournament');
  const query = useFeePlans();
  const pages = useAuthStore((s) => s.visiblePages);
  const actions = useAuthStore((s) => s.allowedActions);
  const canAssign = actions?.includes(templateId ? 'template:update' : 'template:create') ?? false;
  const plans = query.data ?? [];
  const selected = plans.flatMap((p) => p.versions).find((v) => v.id === selectedId);
  const source = current?.sourcePlanVersion;
  const plan = plans.find((p) => p.id === (selected?.planId ?? source?.planId));
  const preview = selected ? { id: selected.id, table: selected.table } : current;
  return (
    <fieldset className="border border-base-300 p-4 space-y-3">
      <legend>{t('feePlans.select')}</legend>
      {pages?.includes('/tournament/timed-fee-plans') && (
        <a className="link" href="/tournament/timed-fee-plans">
          {t('feePlans.manage')}
        </a>
      )}
      {current && (
        <p>
          {source
            ? `${plan?.name ?? t('feePlans.title')} · ${t('feePlans.version')} ${source.sequence}`
            : t('feePlans.legacyPin')}
        </p>
      )}
      {plan?.status === 'ARCHIVED' && <p>{t('feePlans.archivedPin')}</p>}
      {source && plan && plan.latestSequence > source.sequence && !selected && (
        <p>{t('feePlans.newVersion')}</p>
      )}
      <label className="flex flex-col gap-1 min-w-0">
        <span>{t('feePlans.select')}</span>
        <select
          className="w-full border border-base-300 bg-base-100 px-3 py-2 text-base-content focus:outline-2 focus:outline-primary disabled:opacity-60 w-full"
          disabled={!canAssign}
          value={selectedId}
          onChange={(event) =>
            onChange(plans.flatMap((p) => p.versions).find((v) => v.id === event.target.value))
          }
        >
          <option value="">{current ? t('feePlans.keepPin') : t('feePlans.choose')}</option>
          {plans
            .filter((p) => p.status === 'ACTIVE')
            .map((p) => (
              <optgroup key={p.id} label={p.name}>
                {p.versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {p.name} · {t('feePlans.version')} {v.sequence} · {v.table.principalAmount}
                  </option>
                ))}
              </optgroup>
            ))}
        </select>
      </label>
      {query.isError && <p role="alert">{t('feePlans.loadError')}</p>}
      {!query.isLoading && !plans.some((p) => p.status === 'ACTIVE') && (
        <p>{t('feePlans.empty')}</p>
      )}
      {preview && <TimedFeeSummary templateId={templateId} version={preview} />}
    </fieldset>
  );
}
